import { datadogRum } from "@datadog/browser-rum";

// The org's Datadog account is in the AP2 (Sydney) region — the same region the
// ECS FireLens pipeline ships container logs to
// (http-intake.logs.ap2.datadoghq.com, see ADR 0025). A wrong site silently
// drops every event, so it is fixed here rather than left configurable.
export const DATADOG_SITE = "ap2.datadoghq.com";

// Match the FireLens `dd_service` on the web task definition so browser RUM,
// browser logs, the nginx access logs, and any future backend traces line up
// under one service in Datadog.
export const DATADOG_SERVICE = "pet-n-sur-convo-ai-web";

// Shared env/version resolution, reused by Browser Logs (see logger.ts). `||`
// (not `??`) is deliberate: an unset build arg arrives as the empty string and
// must fall back.
export const datadogEnv = (): string => import.meta.env.VITE_DATADOG_ENV || "production";
export const datadogVersion = (): string | undefined =>
  import.meta.env.VITE_APP_VERSION || undefined;

/**
 * Initialise Datadog Browser RUM: client-side telemetry (JavaScript errors,
 * user sessions, page-load performance) that server/nginx logs can never see.
 *
 * RUM is opt-in per build. The application id and client token are baked into
 * the static bundle from `VITE_DATADOG_RUM_APP_ID` /
 * `VITE_DATADOG_RUM_CLIENT_TOKEN`, supplied as Docker build args by the
 * web-*-deployment workflows. When either is missing — local dev, or any build
 * that didn't pass them — this is a no-op, so nothing is sent. The client token
 * is browser-safe (write-only); it is NOT the Datadog API key.
 *
 * Call exactly once, before React renders (see main.tsx).
 */
export function initDatadogRum(): void {
  const applicationId = import.meta.env.VITE_DATADOG_RUM_APP_ID;
  const clientToken = import.meta.env.VITE_DATADOG_RUM_CLIENT_TOKEN;

  // `||`, not `??`: an unset build arg arrives as the empty string, which must
  // fall through to "disabled" rather than initialising RUM without a token.
  if (!applicationId || !clientToken) {
    return;
  }

  datadogRum.init({
    applicationId,
    clientToken,
    site: DATADOG_SITE,
    service: DATADOG_SERVICE,
    env: datadogEnv(),
    version: datadogVersion(),
    // Every session's errors and performance are captured.
    sessionSampleRate: 100,
    // Session Replay is ON for 20% of sessions, and is billed separately. It
    // was raised from 0 directly on the prod/uat branches and backported to
    // main in f826fff; the comment here went on claiming it was off until
    // 2026-07-30.
    sessionReplaySampleRate: 20,
    allowedTracingUrls: [
      "https://api.convoai.supahuman.app"
    ],
    // The app renders PII (emails, call transcripts). `mask-user-input` masks
    // what users type into form fields — it does NOT mask rendered text, so a
    // replay can still show transcript and email content. `mask` would cover
    // rendered text too.
    defaultPrivacyLevel: "mask-user-input",
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
  });
}
